/**
 * Doserne Academy — auth context.
 * JWT is stored in localStorage under DOSERNE_TOKEN.
 * Auth is fully independent from AllStillas platform login.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { LanguageProvider } from "./i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "DOSERNE_TOKEN";

const DoserneAuthContext = createContext(null);

function AuthInner({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/doserne/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
    }
    setLoading(false);
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  // Presence heartbeat — while any Doserne page is open, stamp the user's
  // last_seen_at every 60s so colleagues see them as "online" in the
  // Kolleger tab. Also fires once immediately after auth so the switch
  // from offline → online is instant.
  useEffect(() => {
    if (!user) return undefined;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return undefined;
    const ping = () => {
      axios.post(`${API}/doserne/presence/ping`, { platform: "web" }, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => { /* silent — presence is best-effort */ });
    };
    ping();
    const iv = setInterval(ping, 60_000);
    return () => clearInterval(iv);
  }, [user]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/doserne/login`, { email, password });
    // Clear any stale main-platform admin session in this browser — otherwise a
    // Doserne worker logging in on a machine where an admin was previously
    // signed in would inherit the admin sidebar / privileges visually.
    try {
      localStorage.removeItem("scaffoldx_token");
      localStorage.removeItem("scaffoldx_user");
      localStorage.removeItem("admin_view_as_role");
      localStorage.removeItem("impersonation_info");
    } catch (_e) { /* ignore */ }
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setUser({ ...data.user });
    await bootstrap();
    return data;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  const authHeader = useCallback(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
  }, []);

  return (
    <DoserneAuthContext.Provider value={{ user, loading, login, logout, authHeader, refresh: bootstrap }}>
      {children}
    </DoserneAuthContext.Provider>
  );
}

export function DoserneAuthProvider({ children }) {
  return (
    <LanguageProvider>
      <AuthInner>{children}</AuthInner>
    </LanguageProvider>
  );
}

export const useDoserneAuth = () => useContext(DoserneAuthContext);
export { TOKEN_KEY };
